import React, { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';

import {
  IFichaModalDto,
  CodNegocio,
  helper,
  AnimalDataSet,
  IAnimalData,
  IGenealogiaDto,
  ILactacao,
  IChartDataSet,
  IPdfPayloadBase,
  PdfModalSelectedOptions,
  ReportLayoutItem,
} from '@dinnisoft/esteio-gestao-common';
import TopRelatorioBrancoImg from '@dinnisoft/esteio-gestao-common/assets/imgs/esteio/topo_relatorio_branco.png';
import TopRelatorioImg from '@dinnisoft/esteio-gestao-common/assets/imgs/esteio/topo_relatorio.png';
import { RelatorioPrint } from '@dinnisoft/esteio-gestao-relatorios';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  List,
  Switch,
  Typography,
} from '@mui/material';
import { get } from 'api/api';
import CurvaLactacaoChart, { CurvaLactacaoChartRef } from 'components/Charts/CurvaLactacaoChart';
import CustomModal from 'components/CustomModal';
import SortingTable from 'components/FichaModal/SortingTable';
import Genealogia from 'components/Genealogia';
// Descomentar abaixo para testar
// import { RelatorioPrint } from 'components/Relatorio';
import { useAuth } from 'contexts/auth';
import dayjs from 'dayjs';
import { FichaModalStateProps } from 'types/fichaModal';
import { handleApiError } from 'utils/handleApiError';

import { FichaItemList, getBackgroundColor } from './helpers';
import {
  Container,
  CurvaLactacaoContainer,
  LactacoesLegendContainer,
  LactacoesLegendText,
  ProgressBar,
  ProgressBarStatusText,
} from './styles';

interface FichaModalProps {
  fichaModal: FichaModalStateProps;
  setFichaModal: React.Dispatch<React.SetStateAction<FichaModalStateProps>>;
}

const CORES = [
  '#A07FFF',
  '#D45E43',
  '#7BB429',
  '#8d5b00',
  '#c37d04',
  '#f3a115',
  '#ffdf4c',
  '#bfbeba',
  '#023ec2',
  '#2980ff',
  '#26bcf1',
  '#5fc8ef',
];

const FichaModal: React.FC<FichaModalProps> = ({ fichaModal, setFichaModal }) => {
  const initialPdfModalOptions: PdfModalSelectedOptions = {
    observacoes: true,
    genealogias: true,
    lotes: true,
    inseminacoes: true,
    avGinecologicas: true,
    partos: true,
    lactacoes: true,
    grafico: true,
    controlesLeiteiros: true,
    pesagens: true,
    tratamentos: true,
    vermifugos: true,
    exames: true,
    vacinas: true,
    carrapaticidas: true,
  };

  const { selectedFazenda } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [animalInfo, setAnimalInfo] = useState<IFichaModalDto>({} as IFichaModalDto);
  const [animalData, setAnimalData] = useState<IAnimalData>({
    observacoes: [],
    avGinecologicas: [],
    carrapaticidas: [],
    controlesLeiteiros: [],
    desmames: [],
    exames: [],
    genealogias: {} as IGenealogiaDto,
    inseminacoes: [],
    lactacoes: [],
    lotes: [],
    origens: [],
    partos: [],
    pesagens: [],
    tratamentos: [],
    vacinas: [],
    vermifugos: [],
  });
  const [isCapturing, setIsCapturing] = useState<boolean>(
    selectedFazenda?.codNegocio === CodNegocio.leite ? false : false,
  );
  const [descartado, setDescartado] = useState(false);
  const [progressStatus, setProgressStatus] = useState({
    barraStatus: 0,
    barraStatusText: '',
    backgroundColor: '#f80',
  });
  const [animalDataSet, setAnimalDataSet] = useState<AnimalDataSet[]>([]);
  const codNegocio = selectedFazenda?.codNegocio;
  const [lactacoesDataChart, setLactacoesDataChart] = useState<IChartDataSet[]>([]);

  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  const [pdfModalSelectedOptions, setPdfModalSelectedOptions] =
    useState<PdfModalSelectedOptions>(initialPdfModalOptions);
  const [graficoUri, setGraficoUri] = useState<string>('');
  const [graficoDimensions, setGraficoDimensions] = useState<{ width: number; height: number }>({
    width: 1,
    height: 1,
  });
  const curvaLactacaoChartRef = useRef<CurvaLactacaoChartRef>(null);

  // Fetch animal data
  const fetchFichaAnimal = async (): Promise<void> => {
    if (!selectedFazenda) return;
    setIsLoading(true);

    try {
      if (!codNegocio) {
        throw new Error('Erro ao encontrar codNegócio');
      }

      const response = await get(
        `fichaanimal/codNegocio=${codNegocio}/codAnimal=${fichaModal?.codAnimal}/codFazenda=${selectedFazenda?.codFazenda}`,
      );

      const {
        informacoes,
        observacoes,
        genealogias,
        lotes,
        origens,
        inseminacoes,
        avGinecologicas,
        partos,
        desmames,
        lactacoes,
        controlesLeiteiros,
        pesagens,
        tratamentos,
        vermifugos,
        exames,
        vacinas,
        carrapaticidas,
      } = response.data;

      setAnimalInfo(informacoes);
      setAnimalData({
        observacoes,
        genealogias,
        lotes,
        origens,
        inseminacoes,
        avGinecologicas,
        partos,
        desmames,
        lactacoes,
        controlesLeiteiros,
        pesagens,
        tratamentos,
        vermifugos,
        exames,
        vacinas,
        carrapaticidas,
      });

      if (lactacoes.length > 0) {
        const dataLactacaoTemp: IChartDataSet[] = [];

        lactacoes.forEach((lac: ILactacao, index: number) => {
          const lactacao: IChartDataSet = {
            legenda: `${Number(lac.ordemLactacao)}ª Lactação`,
            svg: { stroke: CORES[index] },
            data: [],
          };

          if (lac.controles && lac.controles.length) {
            lac.controles.forEach((controle: any) => {
              lactacao.data.push({
                x: controle.dataControleLeiteiro,
                y: Number(controle.total),
              });
            });
            dataLactacaoTemp.push(lactacao);
          }
        });

        setLactacoesDataChart(dataLactacaoTemp);
      }
    } catch (error) {
      handleApiError(error);
      toast.error('Erro ao buscar ficha do animal');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);

    try {
      await fetchFichaAnimal();
    } catch (error) {
      toast.error('Erro ao buscar ficha do animal');
    } finally {
      setIsLoading(false);
    }
  };

  const getImageBase64WithDimensions = async (
    imgSrc: string,
  ): Promise<{ base64: string; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // Handle CORS for remote images

      img.onload = () => {
        const { width, height } = img;

        // Create a canvas to draw the image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          // Get the Base64 encoded data URL
          const base64 = canvas.toDataURL('image/png');
          resolve({ base64, width, height });
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = imgSrc;
    });
  };

  const getImageDimensions = (src: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = src;
    });
  };

  // PDF Generation Function
  const printToFile = async () => {
    setIsDownloadingPdf(true);

    const now = new Date();
    const data = dayjs(now).format('DD/MM/YYYY');

    let payload = {
      nomeAnimal: animalInfo.nome,
      brinco: animalInfo.brinco,
      nomeFazenda: selectedFazenda?.pessoaJuridica?.nomeFantasia,
      produtor: selectedFazenda?.nomeProdutor,
      consultor: selectedFazenda?.nomeConsultor,
      codNegocio: selectedFazenda?.codNegocio,
      data,
      animalDataSet,
      origens: animalData.origens,
      desmames: animalData.desmames,
    } as IPdfPayloadBase;

    if (pdfModalSelectedOptions.genealogias) {
      payload.genealogias = [animalData.genealogias];
    }

    if (pdfModalSelectedOptions.observacoes) {
      payload.observacoes = animalData.observacoes;
    }

    if (pdfModalSelectedOptions.lotes) {
      payload.lotes = animalData.lotes;
    }

    if (pdfModalSelectedOptions.inseminacoes) {
      payload.inseminacoes = animalData.inseminacoes;
    }

    if (pdfModalSelectedOptions.avGinecologicas) {
      payload.avGinecologicas = animalData.avGinecologicas;
    }

    if (pdfModalSelectedOptions.partos) {
      payload.partos = animalData.partos;
    }

    if (pdfModalSelectedOptions.pesagens) {
      payload.pesagens = animalData.pesagens;
    }

    if (pdfModalSelectedOptions.tratamentos) {
      payload.tratamentos = animalData.tratamentos;
    }

    if (pdfModalSelectedOptions.vermifugos) {
      payload.vermifugos = animalData.vermifugos;
    }

    if (pdfModalSelectedOptions.exames) {
      payload.exames = animalData.exames;
    }

    if (pdfModalSelectedOptions.vacinas) {
      payload.vacinas = animalData.vacinas;
    }

    if (pdfModalSelectedOptions.carrapaticidas) {
      payload.carrapaticidas = animalData.carrapaticidas;
    }

    if (selectedFazenda?.codNegocio === CodNegocio.leite) {
      payload = {
        ...payload,
        lactacoes: animalData.lactacoes,
        controlesLeiteiros: animalData.controlesLeiteiros,
        options: {
          ...payload.options,
          grafico: lactacoesDataChart.length > 0, // Set grafico para true somente se lactacoesDataChart não for vazio
        },
      };

      if (lactacoesDataChart.length > 0) {
        payload = {
          ...payload,
          graficoUri: graficoUri.replace(/\r\n/g, ''),
          graficoDimensions,
        };
      }
    }

    try {
      // Gera HTML usando o RelatorioPrint
      const report = {
        dados: JSON.stringify(payload), // Converte o payload para uma string JSON
        layoutPdfId: 'animalReportLayout', // Define um layout ID customizado
        nomeProjeto: 'Esteio Gestão',
        nomeRelatorio: 'Ficha do Animal',
      };

      // Instancia o RelatorioPrint com a função getImageDimensions
      const relatorioPrint = new RelatorioPrint(report, getImageDimensions);

      // Define a função callback para obter as imagens do cabeçalho
      relatorioPrint.setHeaderImageCallback(async (isFirstPage) => {
        if (isFirstPage) {
          // Retorna a imagem da primeira página
          return await getImageBase64WithDimensions(TopRelatorioImg);
        } else {
          // Retorna a imagem das páginas subsequentes
          return await getImageBase64WithDimensions(TopRelatorioBrancoImg);
        }
      });

      // Gera o PDF
      const pdfDoc = await relatorioPrint.loadAndGeneratePdf(getLayoutData);

      // Get the PDF as a Blob
      const pdfBlob = pdfDoc.output('blob');

      // Create a URL for the Blob
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open the PDF in a new tab
      window.open(pdfUrl, '_blank');

      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF. Por favor, tente novamente.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const getLayoutData = async (layoutPdfId: string): Promise<ReportLayoutItem[]> => {
    if (layoutPdfId === 'animalReportLayout') {
      const data: ReportLayoutItem[] = [
        {
          title: '', // Opcional
          type: 'subheading',
          items: [
            { key: 'data', label: 'Data' },
            { key: 'nomeFazenda', label: 'Fazenda' },
            { key: 'consultor', label: 'Consultor' },
            { key: 'produtor', label: 'Produtor' },
          ],
        },
        {
          title: `${animalInfo.brinco} - ${animalInfo.nome}`,
          type: 'table',
          src: 'animalDataSet',
          columns: [
            { header: 'Informações', key: 'primary' }, // Primary field (labels)
            { header: '', key: 'secondary' }, // Secondary field (values)
          ],
        },
        {
          title: 'Genealogia',
          type: 'table',
          src: 'genealogias',
          columns: [
            { header: 'Pai', key: 'pai' },
            { header: 'Avô Paterno', key: 'avoPaterno' },
            { header: 'Avó Paterna', key: 'avohPaterna' },
            { header: 'Mãe', key: 'mae' },
            { header: 'Avô Materno', key: 'avoMaterno' },
            { header: 'Avó Materna', key: 'avohMaterna' },
          ],
        },
        {
          title: 'Observações',
          type: 'table',
          src: 'observacoes',
          columns: [
            { header: 'Observação', key: 'descricao' },
            { header: 'Data', key: 'dataObservacao' },
            { header: 'Origem', key: 'origem' },
          ],
        },
        {
          title: 'Lotes',
          type: 'table',
          src: 'lotes',
          columns: [
            { header: 'Nome', key: 'nome' },
            { header: 'Data de Entrada', key: 'dataEntrada' },
            { header: 'Data de Saída', key: 'dataSaida' },
          ],
        },
        {
          title: 'Histórico de transferência',
          type: 'table',
          src: 'origens',
          columns: [
            {
              header: 'Nome',
              key: 'nome',
            },
            {
              header: 'Data de Entrada',
              key: 'dataEntrada',
            },
            {
              header: 'Data de Saída',
              key: 'dataSaida',
            },
          ],
        },
        {
          title: 'Inseminações e transferências de embrião',
          type: 'table',
          src: 'inseminacoes',
          columns: [
            { key: 'data', header: 'Data' },
            { key: 'tipo', header: 'Tipo' },
            { key: 'touro', header: 'Touro/Embrião' },
            { key: 'diagnostico', header: 'Diagnóstico de Gestação' },
            {
              key: 'dataDiagnostico',
              header: 'Data do DG',
              // tooltip: 'Data do Diagnóstico de Gestação',
            },
            { key: 'previsaoParto', header: 'Previsão de Parto' },
            { key: 'motivoInterrupcao', header: 'Interrompido' },
          ],
        },
        {
          title: 'Avaliações Ginecológicas',
          type: 'table',
          src: 'avGinecologicas',
          columns: [
            { key: 'data', header: 'Data' },
            { key: 'uteroStatus', header: 'Útero' },
            { key: 'statusDisplay', header: 'Status Ovário' },
            { key: 'observacao', header: 'Observação' },
          ],
        },
        {
          title: 'Partos',
          type: 'table',
          src: 'partos',
          columns: [
            { key: 'dataParto', header: 'Data' },
            { key: 'dataInseminacao', header: 'Inseminação' },
            { key: 'tipo', header: 'Tipo' },
            { key: 'touro', header: 'Touro/Embrião' },
            { key: 'cria', header: 'Cria' },
            { key: 'sexoCria', header: 'Sexo da cria' },
          ],
        },
        {
          title: selectedFazenda?.codNegocio === CodNegocio.leite ? 'Desaleitamentos' : 'Desmames',
          type: 'table',
          src: 'desmames',
          columns: [
            { key: 'brinco', header: 'Brinco' },
            { key: 'nome', header: 'Nome' },
            { key: 'sexo', header: 'Sexo' },
            { key: 'raca', header: 'Raça' },
            { key: 'dataNasc', header: 'Data Nascimento' },
            { key: 'pesoNasc', header: 'Peso Nasc.' },
            {
              key: 'dataDesaleitamento',
              header: `Data ${
                selectedFazenda?.codNegocio === CodNegocio.leite ? 'Desaleitamento' : 'Desmame'
              }`,
            },
            {
              key: 'idadeDesmame',
              header: `Idade ${
                selectedFazenda?.codNegocio === CodNegocio.leite ? 'Desaleitamento' : 'Desmame'
              } (meses)`,
            },
            {
              key: 'pesoDesmame',
              header: `Peso ${
                selectedFazenda?.codNegocio === CodNegocio.leite ? 'Desaleitamento' : 'Desmame'
              }`,
            },
            { key: 'statusReprodutivoMatriz', header: 'Status Rep. Matriz' },
          ],
        },
      ];

      if (selectedFazenda?.codNegocio === CodNegocio.leite) {
        data.push({
          title: 'Lactações',
          type: 'table',
          src: 'lactacoes',
          columns: [
            { key: 'ordemLactacao', header: 'Número' },
            { key: 'dataInicio', header: 'Início' },
            { key: 'dataFim', header: 'Fim' },
            { key: 'iep', header: 'IEP' },
            { key: 'del', header: 'DEL' },
            { key: 'nControles', header: 'Controles' },
            { key: 'producaoDiaria', header: 'Produção diária' },
            { key: 'pico', header: 'Pico (L)' },
            { key: 'total', header: 'Total' },
          ],
        });

        if (animalData.lactacoes) {
          data.push({
            title: 'Gráfico',
            type: 'image',
            src: 'graficoUri',
            width: 'graficoDimensions.width',
            height: 'graficoDimensions.height',
          });
        }

        data.push({
          title: 'Controles Leiteiros',
          type: 'table',
          src: 'controlesLeiteiros',
          columns: [
            { key: 'data', header: 'Data' },
            { key: 'ordenha1', header: 'Ordenha 1 (L)' },
            { key: 'ordenha2', header: 'Ordenha 2 (L)' },
            { key: 'ordenha3', header: 'Ordenha 3 (L)' },
            { key: 'ordemLactacao', header: 'Lactação' },
            { key: 'total', header: 'Total' },
          ],
        });
      }

      data.push(
        {
          title: 'Pesagens',
          type: 'table',
          src: 'pesagens',
          columns: [
            { key: 'dataPesagem', header: 'Data' },
            { key: 'peso', header: 'Peso (KG)' },
            { key: 'idade', header: 'Idade (Meses)' },
          ],
        },
        {
          title: 'Tratamentos',
          type: 'table',
          src: 'tratamentos',
          columns: [
            { key: 'farmaco', header: 'Fármaco' },
            { key: 'doencas', header: 'Efemeridades' },
            { key: 'dataInicio', header: 'Início' },
            { key: 'dataFim', header: 'Fim' },
          ],
        },
        {
          title: 'Vermifugos',
          type: 'table',
          src: 'vermifugos',
          columns: [
            { key: 'farmaco', header: 'Vermífugo' },
            { key: 'dataInicio', header: 'Data' },
          ],
        },
        {
          title: 'Exames',
          type: 'table',
          src: 'exames',
          columns: [
            { key: 'farmaco', header: 'Exame' },
            { key: 'dataInicio', header: 'Data' },
          ],
        },
        {
          title: 'Vacinas',
          type: 'table',
          src: 'vacinas',
          columns: [
            { key: 'farmaco', header: 'Vacina' },
            { key: 'doencas', header: 'Efemeridades' },
            { key: 'dataInicio', header: 'Data' },
          ],
        },
        {
          title: 'Carrapaticidas',
          type: 'table',
          src: 'carrapaticidas',
          columns: [
            { key: 'farmaco', header: 'Carrapaticida' },
            { key: 'dataInicio', header: 'Data' },
          ],
        },
      );

      return data;
    }
    return [];
  };

  // Função auxiliar para converter imagem para base64
  const convertImageToBase64 = async (imageSrc: string): Promise<string> => {
    const response = await fetch(imageSrc);
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]); // Retorna apenas a parte base64
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const updateProgressStatus = () => {
    const status = helper.getProgressBarStatus(codNegocio, animalInfo, setDescartado);
    setProgressStatus(status);
  };

  const getAnimalDataSet = () => {
    const animalDataSet = helper.getAnimalDataSetJSON(animalInfo, selectedFazenda, descartado);
    setAnimalDataSet(animalDataSet);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateProgressStatus();
    getAnimalDataSet();
  }, [animalInfo]);

  useEffect(() => {
    const captureGrafico = async () => {
      setIsCapturing(true);

      if (
        selectedFazenda?.codNegocio === CodNegocio.leite &&
        curvaLactacaoChartRef.current &&
        lactacoesDataChart.length > 0
      ) {
        try {
          const uri = curvaLactacaoChartRef.current.getChartImageURI();
          const dimensions = curvaLactacaoChartRef.current.getChartDimensions();

          // Calcular a razão de aspecto
          const aspectRatio = dimensions.height / dimensions.width;

          // Definir a nova altura desejada
          const newWidth = 500;

          // Calcular a nova largura proporcional
          const newHeight = newWidth * aspectRatio;

          // Criar as dimensões ajustadas
          const adjustedDimensions = {
            width: newWidth,
            height: newHeight,
          };

          // Atualizar o estado com as dimensões ajustadas
          setGraficoUri(uri);
          setGraficoDimensions(adjustedDimensions);
        } catch (error) {
          console.error('Erro ao capturar o gráfico de lactações:', error);
          toast.error('Erro ao capturar o gráfico de lactações.');
        } finally {
          setIsCapturing(false);
        }
      }
    };

    captureGrafico();
  }, [curvaLactacaoChartRef.current, lactacoesDataChart, selectedFazenda?.codNegocio]);

  return (
    <>
      <CustomModal
        open={fichaModal.open}
        onClose={() => setFichaModal({ open: false, codAnimal: '' })}
        title={`${animalInfo.brinco} - ${animalInfo.nome}`}
        isLoading={isLoading}
      >
        <Container>
          {!isLoading ? (
            <div>
              <Button
                // TODO: Resolver lógica de isCapturing
                // disabled={isCapturing || isDownloadingPdf}
                color="primary"
                type="submit"
                variant="contained"
                size="large"
                sx={{ display: 'flex', gap: 1, ml: 'auto', mb: 2 }}
                onClick={() => setIsPdfModalOpen(true)}
              >
                Exportar PDF
              </Button>

              <ProgressBar
                backgroundColor={progressStatus.backgroundColor}
                variant="determinate"
                value={progressStatus.barraStatus}
                sx={{
                  height: 20,
                  borderRadius: 100,
                  backgroundColor: '#eeeeee',
                }}
              />
              <ProgressBarStatusText>{progressStatus.barraStatusText}</ProgressBarStatusText>
            </div>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              gap="8px"
              marginBottom="1rem"
              textAlign="center"
              width="100%"
            >
              <Skeleton height={20} borderRadius={100} />
              <Skeleton height={24} width={200} />
            </Box>
          )}

          <List disablePadding>
            {!isLoading ? (
              <FichaItemList items={animalDataSet} />
            ) : (
              Array.from({ length: 6 }, (_, i) => i).map((item, i) => (
                <Skeleton
                  highlightColor="#fff"
                  baseColor={getBackgroundColor(i)}
                  key={i}
                  height={48}
                />
              ))
            )}
          </List>

          {/* Content to be captured for PDF */}
          {!isLoading ? (
            <>
              {/* Observações */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="observacoes-content"
                  id="observacoes-header"
                >
                  <Typography color="white" fontSize={20}>
                    Observações
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'descricao', label: 'Observação' },
                      { id: 'dataObservacao', label: 'Data' },
                      { id: 'origem', label: 'Origem' },
                    ]}
                    dados={animalData.observacoes}
                    defaultOrderBy="dataObservacao"
                    defaultOrderDir="desc"
                  />
                </AccordionDetails>
              </Accordion>

              {/* Genealogia */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="genealogia-content"
                  id="genealogia-header"
                >
                  <Typography color="white" fontSize={20}>
                    Genealogia
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Genealogia genealogia={animalData.genealogias} />
                </AccordionDetails>
              </Accordion>

              {/* Lotes */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="lotes-content"
                  id="lotes-header"
                >
                  <Typography color="white" fontSize={20}>
                    Lotes
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'nome', label: 'Nome' },
                      { id: 'dataEntrada', label: 'Data de Entrada' },
                      { id: 'dataSaida', label: 'Data de Saída' },
                    ]}
                    dados={animalData.lotes}
                    defaultOrderBy="dataEntrada"
                    defaultOrderDir="asc"
                  />
                </AccordionDetails>
              </Accordion>

              {/* Histórico de transferência/Origens */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="origens-content"
                  id="origens-header"
                >
                  <Typography color="white" fontSize={20}>
                    Histórico de transferência
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'nome', label: 'Nome' },
                      { id: 'dataEntrada', label: 'Data de Entrada' },
                      { id: 'dataSaida', label: 'Data de Saída' },
                    ]}
                    dados={animalData.origens}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Inseminações */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="inseminacoes-content"
                  id="inseminacoes-header"
                >
                  <Typography color="white" fontSize={20}>
                    Inseminações e transferências de embrião
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'data', label: 'Data' },
                      { id: 'tipo', label: 'Tipo' },
                      { id: 'touro', label: 'Touro/Embrião' },
                      { id: 'diagnostico', label: 'Diagnóstico de Gestação' },
                      {
                        id: 'dataDiagnostico',
                        label: 'Data do DG',
                        tooltip: 'Data do Diagnóstico de Gestação',
                      },
                      { id: 'previsaoParto', label: 'Previsão de Parto' },
                      { id: 'motivoInterrupcao', label: 'Interrompido' },
                    ]}
                    defaultOrderBy="data"
                    defaultOrderDir="asc"
                    dados={animalData.inseminacoes}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Avaliações Ginecológicas */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-ginecologicas-content"
                  id="av-ginecologicas-header"
                >
                  <Typography color="white" fontSize={20}>
                    Avaliações Ginecológicas
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'data', label: 'Data' },
                      { id: 'uteroStatus', label: 'Útero' },
                      { id: 'statusDisplay', label: 'Status Ovário' },
                      { id: 'observacao', label: 'Observação' },
                    ]}
                    defaultOrderBy="data"
                    defaultOrderDir="asc"
                    dados={animalData.avGinecologicas}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Partos */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-partos-content"
                  id="av-partos-header"
                >
                  <Typography color="white" fontSize={20}>
                    Partos
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'dataParto', label: 'Data' },
                      { id: 'dataInseminacao', label: 'Inseminação' },
                      { id: 'tipo', label: 'Tipo' },
                      { id: 'touro', label: 'Touro/Embrião' },
                      { id: 'cria', label: 'Cria' },
                      { id: 'sexoCria', label: 'Sexo da cria' },
                    ]}
                    defaultOrderBy="dataParto"
                    defaultOrderDir="asc"
                    dados={animalData.partos}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Desmames */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-desmames-content"
                  id="av-desmames-header"
                >
                  <Typography color="white" fontSize={20}>
                    {selectedFazenda?.codNegocio === CodNegocio.leite
                      ? 'Desaleitamento'
                      : 'Desmame'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'brinco', label: 'Brinco' },
                      { id: 'nome', label: 'Nome' },
                      { id: 'sexo', label: 'Sexo' },
                      { id: 'raca', label: 'Raça' },
                      { id: 'dataNasc', label: 'Data Nascimento' },
                      { id: 'pesoNasc', label: 'Peso Nasc.' },
                      {
                        id: 'dataDesaleitamento',
                        label: `Data ${
                          selectedFazenda?.codNegocio === CodNegocio.leite
                            ? 'Desaleitamento'
                            : 'Desmame'
                        }`,
                      },
                      {
                        id: 'idadeDesmame',
                        label: `Idade ${
                          selectedFazenda?.codNegocio === CodNegocio.leite
                            ? 'Desaleitamento'
                            : 'Desmame'
                        } (meses)`,
                      },
                      {
                        id: 'pesoDesmame',
                        label: `Peso ${
                          selectedFazenda?.codNegocio === CodNegocio.leite
                            ? 'Desaleitamento'
                            : 'Desmame'
                        }`,
                      },
                      { id: 'statusReprodutivoMatriz', label: 'Status Rep. Matriz' },
                    ]}
                    dados={animalData.desmames}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Lactações */}
              {selectedFazenda?.codNegocio === CodNegocio.leite && (
                <Accordion>
                  <AccordionSummary
                    sx={{ backgroundColor: '#3c8dbc' }}
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    aria-controls="lactacoes-content"
                    id="lactacoes-header"
                  >
                    <Typography color="white" fontSize={20}>
                      Lactações
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    <SortingTable
                      headCells={[
                        { id: 'ordemLactacao', label: 'Número' },
                        { id: 'dataInicio', label: 'Início' },
                        { id: 'dataFim', label: 'Fim' },
                        { id: 'iep', label: 'IEP' },
                        { id: 'del', label: 'DEL' },
                        { id: 'nControles', label: 'Controles' },
                        { id: 'producaoDiaria', label: 'Produção diária' },
                        { id: 'pico', label: 'Pico (L)' },
                        { id: 'total', label: 'Total' },
                      ]}
                      dados={animalData.lactacoes}
                      defaultOrderBy="ordemLactacao"
                      defaultOrderDir="asc"
                    />
                    {animalData.lactacoes.length > 0 && (
                      <CurvaLactacaoContainer>
                        <LactacoesLegendContainer>
                          <LactacoesLegendText>
                            IEP : Intervalo entre partos (meses)
                          </LactacoesLegendText>
                          <LactacoesLegendText>DEL: Dias em Lactação</LactacoesLegendText>
                        </LactacoesLegendContainer>

                        <CurvaLactacaoChart
                          ref={curvaLactacaoChartRef}
                          lactacoesDataChart={lactacoesDataChart}
                        />
                      </CurvaLactacaoContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Controles Leiteiros */}
              {selectedFazenda?.codNegocio === CodNegocio.leite && (
                <Accordion>
                  <AccordionSummary
                    sx={{ backgroundColor: '#3c8dbc' }}
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    aria-controls="controles-leiteiros-content"
                    id="controles-leiteiros-header"
                  >
                    <Typography color="white" fontSize={20}>
                      Controles Leiteiros
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0 }}>
                    <SortingTable
                      headCells={[
                        { id: 'data', label: 'Data' },
                        { id: 'ordenha1', label: 'Ordenha 1 (L)' },
                        { id: 'ordenha2', label: 'Ordenha 2 (L)' },
                        { id: 'ordenha3', label: 'Ordenha 3 (L)' },
                        { id: 'ordemLactacao', label: 'Lactação' },
                        { id: 'total', label: 'Total' },
                      ]}
                      dados={animalData.controlesLeiteiros}
                      defaultOrderBy="data"
                      defaultOrderDir="asc"
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Pesagens */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-pesagens-content"
                  id="av-pesagens-header"
                >
                  <Typography color="white" fontSize={20}>
                    Pesagens
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'dataPesagem', label: 'Data' },
                      { id: 'peso', label: 'Peso (KG)' },
                      { id: 'idade', label: 'Idade (Meses)' },
                    ]}
                    defaultOrderBy="dataPesagem"
                    defaultOrderDir="asc"
                    dados={animalData.pesagens}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Tratamentos */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-tratamentos-content"
                  id="av-tratamentos-header"
                >
                  <Typography color="white" fontSize={20}>
                    Tratamentos
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'farmaco', label: 'Fármaco' },
                      { id: 'doencas', label: 'Efemeridades' },
                      { id: 'dataInicio', label: 'Início' },
                      { id: 'dataFim', label: 'Fim' },
                    ]}
                    defaultOrderBy="farmaco"
                    defaultOrderDir="asc"
                    dados={animalData.tratamentos}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Vermífugos */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-vermifugos-content"
                  id="av-vermifugos-header"
                >
                  <Typography color="white" fontSize={20}>
                    Vermífugos
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'farmaco', label: 'Vermífugo' },
                      { id: 'dataInicio', label: 'Data' },
                    ]}
                    defaultOrderBy="farmaco"
                    defaultOrderDir="asc"
                    dados={animalData.vermifugos}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Exames */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-exames-content"
                  id="av-exames-header"
                >
                  <Typography color="white" fontSize={20}>
                    Exames
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'farmaco', label: 'Exame' },
                      { id: 'dataInicio', label: 'Data' },
                    ]}
                    defaultOrderBy="farmaco"
                    defaultOrderDir="asc"
                    dados={animalData.exames}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Vacinas */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-vacinas-content"
                  id="av-vacinas-header"
                >
                  <Typography color="white" fontSize={20}>
                    Vacinas
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'farmaco', label: 'Vacina' },
                      { id: 'doencas', label: 'Efemeridades' },
                      { id: 'dataInicio', label: 'Data' },
                    ]}
                    defaultOrderBy="farmaco"
                    defaultOrderDir="asc"
                    dados={animalData.vacinas}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Carrapaticidas */}
              <Accordion>
                <AccordionSummary
                  sx={{ backgroundColor: '#3c8dbc' }}
                  expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                  aria-controls="av-carrapaticidas-content"
                  id="av-carrapaticidas-header"
                >
                  <Typography color="white" fontSize={20}>
                    Carrapaticidas
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <SortingTable
                    headCells={[
                      { id: 'farmaco', label: 'Carrapaticida' },
                      { id: 'dataInicio', label: 'Data' },
                    ]}
                    defaultOrderBy="farmaco"
                    defaultOrderDir="asc"
                    dados={animalData.carrapaticidas}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            Array.from({ length: 14 }, (_, i) => i).map((item, i) => (
              <Box key={i} display="flex" flexDirection="column" gap={20}>
                <Skeleton height={54} />
              </Box>
            ))
          )}
        </Container>
      </CustomModal>
      <CustomModal
        open={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        title={animalInfo.brinco}
        description={animalInfo.nome}
        isLoading={isLoading}
      >
        <Container>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            size="large"
            sx={{ display: 'flex', gap: 1, ml: 'auto', mb: 2 }}
            onClick={printToFile}
          >
            {isDownloadingPdf ? <CircularProgress color="inherit" size={24} /> : 'Gerar PDF'}
          </Button>
          <Grid container spacing={2}>
            {/* Observações */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Observações"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.observacoes}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        observacoes: !prevValues.observacoes,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Genealogia */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Genealogia"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.genealogias}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        genealogias: !prevValues.genealogias,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Lotes */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Lotes"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.lotes}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        lotes: !prevValues.lotes,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Inseminações e Transferências de embrião */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Inseminações e Transferências de embrião"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.inseminacoes}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        inseminacoes: !prevValues.inseminacoes,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* DG/Aval. Gin. */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="DG/Aval. Gin."
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.avGinecologicas}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        avGinecologicas: !prevValues.avGinecologicas,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Condicional para CodNegocio.leite */}
            {selectedFazenda?.codNegocio === CodNegocio.leite && (
              <>
                {/* Lactações */}
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControlLabel
                    label="Lactações"
                    control={
                      <Switch
                        checked={pdfModalSelectedOptions.lactacoes}
                        onChange={() =>
                          setPdfModalSelectedOptions((prevValues) => ({
                            ...prevValues,
                            lactacoes: !prevValues.lactacoes,
                          }))
                        }
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                  />
                </Grid>
                {/* Gráfico */}
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControlLabel
                    label="Gráfico"
                    control={
                      <Switch
                        checked={pdfModalSelectedOptions.grafico}
                        onChange={() =>
                          setPdfModalSelectedOptions((prevValues) => ({
                            ...prevValues,
                            grafico: !prevValues.grafico,
                          }))
                        }
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                  />
                </Grid>
                {/* Controles Leiteiros */}
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControlLabel
                    label="Controles Leiteiros"
                    control={
                      <Switch
                        checked={pdfModalSelectedOptions.controlesLeiteiros}
                        onChange={() =>
                          setPdfModalSelectedOptions((prevValues) => ({
                            ...prevValues,
                            controlesLeiteiros: !prevValues.controlesLeiteiros,
                          }))
                        }
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                  />
                </Grid>
              </>
            )}
            {/* Pesagens */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Pesagens"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.pesagens}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        pesagens: !prevValues.pesagens,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Tratamentos */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Tratamentos"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.tratamentos}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        tratamentos: !prevValues.tratamentos,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Vermífugos */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Vermífugos"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.vermifugos}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        vermifugos: !prevValues.vermifugos,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Exames */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Exames"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.exames}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        exames: !prevValues.exames,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Vacinas */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Vacinas"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.vacinas}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        vacinas: !prevValues.vacinas,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
            {/* Carrapaticidas */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                label="Carrapaticidas"
                control={
                  <Switch
                    checked={pdfModalSelectedOptions.carrapaticidas}
                    onChange={() =>
                      setPdfModalSelectedOptions((prevValues) => ({
                        ...prevValues,
                        carrapaticidas: !prevValues.carrapaticidas,
                      }))
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Container>
      </CustomModal>
    </>
  );
};

export default FichaModal;
